import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import { differenceInDays, format, setYear, startOfMonth } from 'date-fns';
import { ko } from 'date-fns/locale';
import { DateRange } from 'react-day-picker';

import {
  calendarHeader,
  calendarInputField,
  calendarInputLabel,
  calendarInputRow,
  calendarPanel,
  calendarPeriodText,
  calendarWeekDay,
  calendarWeekHeader,
  datePickerContainer,
  datePickerContent,
  datePickerFooter,
} from './DatePicker.css';
import { DatePickerCalendar, DatePickerCalendarRef } from './DatePickerCalendar';
import { DatePickerPreset } from './DatePickerPreset';
import { DatePickerPresetGroup, PresetOption, RangeDatePickerProps } from './types';
import { createDateBlurHandler, createDateInputHandler, createDateKeyDownHandler } from './utils';
import { Button } from '../Button';
import { Input } from '../Input';

const WEEKDAYS = ['월', '화', '수', '목', '금', '토', '일'];

type RangeDatePickerPanelProps = Omit<
  RangeDatePickerProps,
  'mode' | 'customPresetGroups' | 'defaultPresetOptions'
> & {
  presetGroups: DatePickerPresetGroup[];
  presetYear: number;
  onPresetYearChange: (year: number) => void;
};

export const RangeDatePickerPanel = forwardRef<HTMLDivElement, RangeDatePickerPanelProps>(
  (
    {
      value,
      onChange,
      minDate,
      maxDate,
      showPresets = true,
      presetGroups,
      presetYear,
      onPresetYearChange,
      className,
      onClose,
      onApply,
    },
    ref,
  ) => {
    const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(value);
    const [startInputValue, setStartInputValue] = useState<string>(
      value ? formatDate(value.from) : '',
    );
    const [endInputValue, setEndInputValue] = useState<string>(value ? formatDate(value.to) : '');
    const prevStartValueRef = useRef<string>('');
    const prevEndValueRef = useRef<string>('');
    const isStartFocusedRef = useRef<boolean>(false);
    const isEndFocusedRef = useRef<boolean>(false);
    const calendarRef = useRef<DatePickerCalendarRef | null>(null);
    const startInputRef = useRef<HTMLInputElement | null>(null);
    const endInputRef = useRef<HTMLInputElement | null>(null);
    const isMountedRef = useRef<boolean>(false);
    const initialMonth = useMemo(() => value?.from || new Date(), [value]);

    // Handle year change
    const handleYearChange = (newYear: number) => {
      const currentViewportMonth = calendarRef.current?.getCurrentViewportMonth();

      if (!currentViewportMonth) {
        // If we can't determine current viewport month, just update the year
        onPresetYearChange(newYear);
        return;
      }

      const currentYear = currentViewportMonth.getFullYear();
      const hasYearInViewport = currentYear === newYear;

      if (!hasYearInViewport) {
        // Reset months with the new year but keep the same month
        const newBaseMonth = setYear(currentViewportMonth, newYear);
        calendarRef.current?.resetMonths(newBaseMonth);

        // Scroll to the same month after reset (wait for DOM update)
        const targetMonth = startOfMonth(newBaseMonth);
        const attemptScroll = () => {
          const success = calendarRef.current?.scrollToMonth(targetMonth);
          if (!success) {
            // Retry if element is not ready yet
            requestAnimationFrame(() => {
              const retrySuccess = calendarRef.current?.scrollToMonth(targetMonth);
              if (!retrySuccess) {
                // One more retry
                setTimeout(() => {
                  calendarRef.current?.scrollToMonth(targetMonth);
                }, 0);
              }
            });
          }
        };
        requestAnimationFrame(attemptScroll);
      }

      onPresetYearChange(newYear);
    };

    const handleSelect = (range: DateRange | undefined) => {
      if (range && 'from' in range) {
        setSelectedRange(range);

        const targetMonth =
          range.from && range.from !== selectedRange?.from
            ? range.from
            : range.to && range.to !== selectedRange?.to
              ? range.to
              : undefined;

        if (targetMonth) {
          calendarRef.current?.resetMonths(targetMonth);
          calendarRef.current?.scrollToMonth(targetMonth);
        }
      }
    };

    const handlePresetSelect = (preset: PresetOption) => {
      const range = preset.getValue();
      setSelectedRange(range);
    };

    const handleClose = () => {
      onClose?.();
    };

    const handleApply = () => {
      onChange?.(selectedRange);
      onApply?.(selectedRange);
      onClose?.();
    };

    // 시작일 Input의 handleChange
    const handleStartChange = createDateInputHandler(setStartInputValue, (date) => {
      setSelectedRange({
        from: date,
        to: selectedRange?.to,
      });
    });

    // 시작일 Input의 handleKeyDown
    const handleStartKeyDown = createDateKeyDownHandler(
      startInputValue || formatDate(selectedRange?.from),
      setStartInputValue,
    );

    // 종료일 Input의 handleChange
    const handleEndChange = createDateInputHandler(setEndInputValue, (date) => {
      setSelectedRange({
        from: selectedRange?.from,
        to: date,
      });
    });

    // 종료일 Input의 handleKeyDown
    const handleEndKeyDown = createDateKeyDownHandler(
      endInputValue || formatDate(selectedRange?.to),
      setEndInputValue,
    );

    // 시작일 Input의 handleBlur
    const handleStartBlur = () => {
      isStartFocusedRef.current = false;
      const blurHandler = createDateBlurHandler({
        inputValue: startInputValue,
        onReset: () => setStartInputValue(formatDate(selectedRange?.from)),
        setDate: (date) =>
          handleSelect({
            from: date,
            to: selectedRange?.to,
          }),
        validationOptions: {
          minDate,
          maxDate,
          rangeEnd: selectedRange?.to,
        },
      });
      blurHandler();
    };

    // 종료일 Input의 handleBlur
    const handleEndBlur = () => {
      isEndFocusedRef.current = false;
      const blurHandler = createDateBlurHandler({
        inputValue: endInputValue,
        onReset: () => setEndInputValue(formatDate(selectedRange?.to)),
        setDate: (date) =>
          handleSelect({
            from: selectedRange?.from,
            to: date,
          }),
        validationOptions: {
          minDate,
          maxDate,
          rangeStart: selectedRange?.from,
        },
      });
      blurHandler();
    };

    // Input에 포커스가 갔을 때 현재 값을 입력값으로 설정
    const handleStartFocus = () => {
      isStartFocusedRef.current = true;
      const currentValue = formatDate(selectedRange?.from);
      setStartInputValue(currentValue);
      prevStartValueRef.current = currentValue;
    };

    const handleEndFocus = () => {
      isEndFocusedRef.current = true;
      const currentValue = formatDate(selectedRange?.to);
      setEndInputValue(currentValue);
      prevEndValueRef.current = currentValue;
    };

    const getPeriodText = () => {
      if (!selectedRange?.from || !selectedRange?.to) {
        return '선택기간 0일';
      }
      const days = differenceInDays(selectedRange.to, selectedRange.from) + 1;
      return `선택기간 ${days}일`;
    };

    // selectedRange가 외부에서 변경될 때 inputValue 동기화 (포커스가 없을 때만)
    useEffect(() => {
      if (!isStartFocusedRef.current) {
        setStartInputValue(formatDate(selectedRange?.from));
      }
      if (!isEndFocusedRef.current) {
        setEndInputValue(formatDate(selectedRange?.to));
      }
    }, [selectedRange]);

    // 최초 마운트 시 startInput에 포커스, 기존 값이 있으면 텍스트 선택
    useEffect(() => {
      if (!isMountedRef.current) {
        isMountedRef.current = true;
        // DOM이 준비된 후 포커스 및 선택
        setTimeout(() => {
          if (startInputRef.current) {
            startInputRef.current.focus();
            startInputRef.current.select();
          }
        });
      }
    }, []);

    return (
      <div ref={ref} className={clsx(datePickerContainer, className)}>
        <div className={datePickerContent({ mode: 'range' })}>
          {showPresets && (
            <DatePickerPreset
              presetGroups={presetGroups}
              selectedRange={selectedRange}
              onSelect={handlePresetSelect}
              year={presetYear}
              onYearChange={handleYearChange}
            />
          )}

          <div className={calendarPanel}>
            <div className={calendarHeader}>
              <div className={calendarInputRow}>
                <div className={calendarInputField}>
                  <span className={calendarInputLabel}>시작</span>
                  <Input
                    ref={startInputRef}
                    value={startInputValue}
                    onChange={handleStartChange}
                    onKeyDown={handleStartKeyDown}
                    onBlur={handleStartBlur}
                    onFocus={handleStartFocus}
                    inputSize="m"
                    placeholder={formatDate(new Date())}
                  />
                </div>
                <div className={calendarInputField}>
                  <span className={calendarInputLabel}>종료</span>
                  <Input
                    ref={endInputRef}
                    value={endInputValue}
                    onChange={handleEndChange}
                    onKeyDown={handleEndKeyDown}
                    onBlur={handleEndBlur}
                    onFocus={handleEndFocus}
                    inputSize="m"
                    placeholder={formatDate(new Date())}
                  />
                </div>
              </div>

              <span className={calendarPeriodText}>{getPeriodText()}</span>

              <div className={calendarWeekHeader}>
                {WEEKDAYS.map((day) => (
                  <span key={day} className={calendarWeekDay}>
                    {day}
                  </span>
                ))}
              </div>
            </div>

            <DatePickerCalendar
              ref={calendarRef}
              mode="range"
              selected={selectedRange}
              onSelect={handleSelect}
              minDate={minDate}
              maxDate={maxDate}
              initialMonth={initialMonth}
            />
          </div>
        </div>

        <div className={datePickerFooter}>
          <Button buttonType="border" buttonSize="l" onClick={handleClose}>
            닫기
          </Button>
          <Button buttonType="primary" buttonSize="l" onClick={handleApply}>
            적용
          </Button>
        </div>
      </div>
    );
  },
);

RangeDatePickerPanel.displayName = 'RangeDatePickerPanel';

const formatDate = (date: Date | undefined) => {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd', { locale: ko });
};
